import type { DocumentKind } from "@haulsheet/core";
import { apiFetch, API_URL, ApiClientError } from "./client";

/** Blueprint §6 / A13: max upload size (must match API zod). */
export const DOCUMENT_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

export const DOCUMENT_UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "application/pdf",
] as const;

export type DocumentUploadContentType =
  (typeof DOCUMENT_UPLOAD_CONTENT_TYPES)[number];

export const DOCUMENT_KIND_OPTIONS: ReadonlyArray<{
  value: DocumentKind;
  label: string;
}> = [
  { value: "ratecon", label: "Rate con" },
  { value: "bol", label: "BOL" },
  { value: "pod", label: "POD" },
  { value: "screenshot", label: "Screenshot" },
  { value: "fuel_receipt", label: "Fuel receipt" },
  { value: "maintenance_receipt", label: "Maintenance" },
  { value: "other", label: "Other" },
];

export interface UploadDocumentInput {
  file: File;
  kind: DocumentKind;
  loadId?: string | null;
}

export interface UploadDocumentResult {
  documentId: string;
  storageKey: string;
  contentType: DocumentUploadContentType;
  bytes: number;
  kind: DocumentKind;
  loadId: string | null;
}

interface UploadUrlResponse {
  documentId: string;
  storageKey: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresInSec: number;
  expiresAt: string;
}

function isAllowedContentType(t: string): t is DocumentUploadContentType {
  return (DOCUMENT_UPLOAD_CONTENT_TYPES as readonly string[]).includes(t);
}

/**
 * Map a File to an API-allowed content type. Prefer file.type; fall back to
 * extension for HEIC/PDF when the browser leaves type blank.
 */
export function resolveDocumentContentType(
  file: File,
): DocumentUploadContentType {
  const raw = (file.type || "").toLowerCase().trim();
  if (raw === "image/jpg") return "image/jpeg";
  if (isAllowedContentType(raw)) return raw;

  const name = file.name.toLowerCase();
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".heic") || name.endsWith(".heif")) return "image/heic";
  if (name.endsWith(".pdf")) return "application/pdf";

  throw new ApiClientError(
    400,
    "VALIDATION",
    "Unsupported file type. Use JPEG, PNG, HEIC, or PDF.",
  );
}

/** API answer when the server runs with STORAGE_DISABLED (no R2/S3 yet). */
export const STORAGE_UNAVAILABLE_MESSAGE = "Document storage is not set up on this server yet";

export function isStorageUnavailable(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 503 && err.code === "UNAVAILABLE";
}

/** What the upload UI shows for a failed upload. */
export function uploadErrorMessage(err: unknown): string {
  if (isStorageUnavailable(err)) {
    return (err as ApiClientError).message || STORAGE_UNAVAILABLE_MESSAGE;
  }
  if (err instanceof ApiClientError || err instanceof Error) return err.message || "Upload failed";
  return "Upload failed";
}

/**
 * A13 upload flow: (1) POST /documents/upload-url → (2) PUT bytes to the
 * presigned URL with the signed Content-Type → (3) POST /documents/:id/complete.
 * Storage keys are org-scoped on the server (orgs/<org>/docs/<id>).
 */
export async function uploadDocument(
  input: UploadDocumentInput,
): Promise<UploadDocumentResult> {
  const { file, kind } = input;
  if (file.size <= 0) {
    throw new ApiClientError(400, "VALIDATION", "File is empty");
  }
  if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
    throw new ApiClientError(
      400,
      "VALIDATION",
      "File exceeds the 15 MB limit",
    );
  }

  const contentType = resolveDocumentContentType(file);
  const bytes = file.size;
  const loadId = input.loadId ?? null;

  const signed = await apiFetch<UploadUrlResponse>(
    "/api/v1/documents/upload-url",
    {
      method: "POST",
      body: JSON.stringify({
        contentType,
        bytes,
        kind,
        ...(loadId ? { loadId } : {}),
      }),
    },
  );

  // PUT goes to S3/R2 or the same-origin stub. Do not send cookies to S3;
  // for stub URLs (relative or same API host) credentials are fine.
  const putUrl = signed.uploadUrl.startsWith("http")
    ? signed.uploadUrl
    : `${API_URL}${signed.uploadUrl}`;
  const putHeaders = new Headers(signed.headers ?? {});
  if (!putHeaders.has("Content-Type")) {
    putHeaders.set("Content-Type", contentType);
  }
  // Content-Length is signed for S3; the browser sets it from the body.
  // Do not set it manually (forbidden request header in browsers).

  const putRes = await fetch(putUrl, {
    method: "PUT",
    headers: putHeaders,
    body: file,
    // Stub is same-origin and may need cookies skipped; S3 must not get them.
    credentials: putUrl.includes("/stub-upload/") ? "omit" : "omit",
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new ApiClientError(
      putRes.status,
      "UPLOAD_FAILED",
      text || `Upload failed (${putRes.status})`,
    );
  }

  await apiFetch<{ ok: boolean; documentId: string }>(
    `/api/v1/documents/${signed.documentId}/complete`,
    {
      method: "POST",
      body: JSON.stringify({
        storageKey: signed.storageKey,
        contentType,
        bytes,
        kind,
        loadId,
      }),
    },
  );

  return {
    documentId: signed.documentId,
    storageKey: signed.storageKey,
    contentType,
    bytes,
    kind,
    loadId,
  };
}
